import React, { useEffect, useContext, useState } from "react";
import {
  MantineProvider,
  LoadingOverlay,
  useMantineTheme,
} from "@mantine/core";

// Function to detect system color scheme preference (same as in root.js)
function getSystemColorScheme() {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

import { Notifications } from "@mantine/notifications";
import { DataContext } from "./context/DataContext";
import Navigation from "./components/navigation";
import FontLoaderSimple from "./components/FontLoaderSimple";
import AppFooter from "./components/app-footer";
import { isMobile } from "react-device-detect";
const isIframe = window !== window.top;
import "./App.css";

const FALLBACK_STACK =
  'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

function familyStack(family) {
  if (!family) return FALLBACK_STACK;
  const quoted = family.includes(" ") ? `"${family}"` : family;
  return `${quoted}, ${FALLBACK_STACK}`;
}

// Inner component that can use the theme
function AppContent({ routes }) {
  const { data } = useContext(DataContext);
  const theme = useMantineTheme();
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 640);

  // Update the viewport height and screen size on resize
  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
      setIsSmallScreen(window.innerWidth <= 640);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Theme-aware styles
  const getDesktopContainerStyles = () => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
  });

  const getContentStylesDirectDesktop = () => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "400px",
    border: `.5px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
    padding: "25px 25px",
    paddingTop: "10px",
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
  });

  return (
    <>
      <Notifications position="top-center" zIndex={2077} />
      <div 
        className={!isIframe && !isMobile && !isSmallScreen ? "desktopContainer" : ""} 
        style={{
          ...(!isIframe && !isMobile && !isSmallScreen ? getDesktopContainerStyles() : {}),
          ...(isMobile || isIframe || isSmallScreen ? { 
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white 
          } : {})
        }}
      >
        <div
          style={{
            ...(isMobile || isSmallScreen ? { 
              height: `calc(${viewportHeight}px - 50px)`,
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white
            } : {}),
            ...(!isIframe && !isMobile && !isSmallScreen ? getContentStylesDirectDesktop() : {})
          }}
          className={
            !isIframe && !isMobile && !isSmallScreen ? "contentStylesDirectDesktop" : "content"
          }
        >
          <Navigation logo_url={data?.logo_url ? data.logo_url : ""} />
          {routes}
        </div>
      </div>

      <AppFooter />
    </>
  );
}

function App({ routes }) {
  const { data } = useContext(DataContext);
  
  console.log('🎨 App Component Render:', {
    hasData: !!data,
    colorScheme: data?.styles?.colorScheme,
    systemColorScheme: getSystemColorScheme(),
    timestamp: new Date().toISOString()
  });

  if (!data) {
    console.log('🎨 App: No data, showing loading with system theme:', getSystemColorScheme());
    return (
      <MantineProvider theme={{ colorScheme: getSystemColorScheme() }} withGlobalStyles withNormalizeCSS>
        <LoadingOverlay visible />
      </MantineProvider>
    );
  }
  const buttonTextColor = data.styles?.components?.button?.color || null;
  
  // Font configuration
  const fontDef = data.styles?.global?.typography?.font || null;
  const fontStack = familyStack(fontDef?.family);

  const colors_array = data.styles?.colors
    ? data.styles.colors
    : {
        "vx-green": [
          "#eff6fb",
          "#dee9f1",
          "#b8d2e5",
          "#8fbad8",
          "#6ea5ce",
          "#5998c8",
          "#4d92c6",
          "#3e7eaf",
          "#34709d",
          "#22618b",
        ],
      };

  // Debug theme configuration
  // Use system color scheme as fallback when data doesn't specify
  const finalColorScheme = data.styles?.colorScheme || getSystemColorScheme();
  
  console.log('🎨 App: Creating MantineProvider with theme:', {
    dataColorScheme: data.styles?.colorScheme,
    systemColorScheme: getSystemColorScheme(),
    finalColorScheme: finalColorScheme,
    timestamp: new Date().toISOString()
  });
  
  const themeConfig = {
    //fontFamily: "Roboto, sans-serif",
    colors: colors_array,
    colorScheme: finalColorScheme,
  };

  return (
    <MantineProvider
      theme={{
        ...themeConfig,
        primaryColor: data.styles?.primaryColor
          ? data.styles.primaryColor
          : "vx-green",
        primaryShade: data.styles?.primaryShade ? data.styles.primaryShade : 5,
        // Use custom family globally when provided (text + headings)
        fontFamily: fontDef ? fontStack : undefined,
        headings: fontDef ? { fontFamily: fontStack } : undefined,
        components: {
          Button: {
            defaultProps: {
              radius: data.styles?.button?.radius
                ? data.styles.button.radius
                : 6,
            },
            styles: (theme, params) => {
              // Default variant is "filled" when undefined
              const isFilledVariant = params.variant === "filled" || params.variant === undefined;
              
              return {
                root: {
                  // Apply to filled (primary) buttons when a color is provided
                  // Note: undefined variant is treated as "filled" (default)
                  ...(isFilledVariant && buttonTextColor
                    ? {
                        color: buttonTextColor,
                        // keep icons in sync
                        "& svg": { color: buttonTextColor, fill: "currentColor" },
                      }
                    : {}),
                },
              };
            },
          },
        },
        defaultRadius: data.styles?.defaultRadius ? data.defaultRadius : 6,
        breakpoints: {
          xs: 340, // Extra small screen breakpoint in pixels
          sm: 640, // Small screen breakpoint in pixels
          md: 960, // Medium screen breakpoint in pixels
          lg: 1280, // Large screen breakpoint in pixels
          xl: 1440, // Extra large screen breakpoint in pixels
        },
      }}
      withGlobalStyles
      withNormalizeCSS
    >
      {fontDef && <FontLoaderSimple styles={data.styles} />}
      <AppContent routes={routes} />
    </MantineProvider>
  );
}

export default App;
